import axios from "axios";
import { API_ROOT } from ".";

const EVENT_TICKET_ADMIN_API_URL = `${API_ROOT}/event-tickets/admin`;

function getAuthHeaders(): { Authorization: string } {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Not authenticated");
  return { Authorization: `Bearer ${token}` };
}

export interface AdminEventSummary {
  _id: string;
  eventName: string;
  eventStartTime: string;
  eventEndTime: string;
  eventLocation: string;
  availableTicketCount?: number;
  ticketsSold: number;
  purchaseCount: number;
}

export interface AdminEventTicketPurchase {
  buyerName: string;
  buyerEmail: string;
  quantity: number;
  confirmationCode: string;
  comment: string;
  purchasedAt: string;
}

export interface AdminEventTicketDetails {
  event: {
    _id: string;
    eventName: string;
    eventStartTime: string;
    eventEndTime: string;
    eventLocation: string;
    availableTicketCount?: number;
  };
  ticketsSold: number;
  purchaseCount: number;
  purchases: AdminEventTicketPurchase[];
}

type AdminEventSummaryApiResponse = {
  _id: string;
  eventName: string;
  eventStartTime: string;
  eventEndTime: string;
  locationOfEvent: string;
  numberOfAvailableTickets?: number;
  ticketsSold: number;
  purchaseCount: number;
};

type AdminEventTicketDetailsApiResponse = {
  event: {
    _id: string;
    eventName: string;
    eventStartTime: string;
    eventEndTime: string;
    locationOfEvent: string;
    numberOfAvailableTickets?: number;
  };
  ticketsSold: number;
  purchaseCount: number;
  purchases: Array<{
    buyerName: string;
    buyerEmail: string;
    quantity: number;
    confirmationCode: string;
    comment?: string;
    purchasedAt: string;
  }>;
};

function mapAdminEventSummaryFromApi(
  raw: AdminEventSummaryApiResponse,
): AdminEventSummary {
  return {
    _id: raw._id,
    eventName: raw.eventName,
    eventStartTime: raw.eventStartTime,
    eventEndTime: raw.eventEndTime,
    eventLocation: raw.locationOfEvent,
    availableTicketCount: raw.numberOfAvailableTickets,
    ticketsSold: raw.ticketsSold,
    purchaseCount: raw.purchaseCount,
  };
}

function mapAdminEventTicketDetailsFromApi(
  raw: AdminEventTicketDetailsApiResponse,
): AdminEventTicketDetails {
  return {
    event: {
      _id: raw.event._id,
      eventName: raw.event.eventName,
      eventStartTime: raw.event.eventStartTime,
      eventEndTime: raw.event.eventEndTime,
      eventLocation: raw.event.locationOfEvent,
      availableTicketCount: raw.event.numberOfAvailableTickets,
    },
    ticketsSold: raw.ticketsSold,
    purchaseCount: raw.purchaseCount,
    purchases: raw.purchases.map((purchase) => ({
      buyerName: purchase.buyerName,
      buyerEmail: purchase.buyerEmail,
      quantity: purchase.quantity,
      confirmationCode: purchase.confirmationCode,
      comment: purchase.comment ?? "",
      purchasedAt: purchase.purchasedAt,
    })),
  };
}

export async function callFetchAdminEventSummaries(): Promise<AdminEventSummary[]> {
  const { data } = await axios.get<AdminEventSummaryApiResponse[]>(
    `${EVENT_TICKET_ADMIN_API_URL}/events`,
    { headers: getAuthHeaders() },
  );
  return data.map(mapAdminEventSummaryFromApi);
}

export async function callFetchAdminEventTicketDetails(
  eventId: string,
): Promise<AdminEventTicketDetails> {
  const { data } = await axios.get<AdminEventTicketDetailsApiResponse>(
    `${EVENT_TICKET_ADMIN_API_URL}/events/${eventId}`,
    { headers: getAuthHeaders() },
  );
  return mapAdminEventTicketDetailsFromApi(data);
}
